import { mapValues } from 'es-toolkit';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Box, Checkbox, FormControlLabel, FormGroup, MenuItem, Select, SelectChangeEvent } from '@mui/material';

import { ComponentQuantityList, getLayoutQuantityMap, getQuantityMap, QuantityMap } from '@paragon/layout-models';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import {
  getScopedComponentDesignQuantities,
  ProjectActionScope,
  ScopeQuantityBasis,
} from '../../store/projectActionScope';
import { RootState } from '../../store/slices';
import { useAppSelector } from '../hooks';
import { CountFileUploader } from './CountFileUploader';

export function useScopedQuantitiesState(scope: ProjectActionScope) {
  const quantityMap = useAppSelector(getQuantityMap);

  return useState(() => getScopedComponentDesignQuantities(scope, quantityMap, 'fullQuantity'));
}

export function useQuantitiesAndOptionState(scope: ProjectActionScope) {
  return [...useScopedQuantitiesState(scope), ...useState(QuantityOption.Complete)] as const;
}

type OwnProps = {
  scope: ProjectActionScope;
  quantityOption: QuantityOption;
  quantities: { [guid: string]: number };
  onQuantityChange: (quantityOption: QuantityOption, quantities: { [guid: string]: number }) => void;
};

type Props = StateProps & OwnProps;
type State = {
  layoutQuantitiesApplicable: boolean;
  onlyUseLayoutQuantities: boolean;
};

export enum QuantityOption {
  Complete = 'Complete',
  OneOfTopLevel = 'OneOfTopLevel',
  Custom = 'Custom',
}

class ChooseQuantitiesComponent extends React.Component<Props, State> {
  state = {
    layoutQuantitiesApplicable: false,
    onlyUseLayoutQuantities: false,
  };

  componentDidMount(): void {
    this.onChangeOptions({ mounting: true });
  }

  onChangeQuantityOption = (event: SelectChangeEvent<QuantityOption>) => {
    this.onChangeOptions({ quantity: event.target.value as QuantityOption });
  };

  onChangeOptions = (
    options: {
      quantity?: QuantityOption;
      mounting?: boolean;
    } = {},
  ) => {
    const { layoutQuantityMap, overallQuantityMap, scope } = this.props;

    const quantitiesFor = (quantityMap: QuantityMap, basis: ScopeQuantityBasis) =>
      getScopedComponentDesignQuantities(scope, quantityMap, basis);

    let overallQuantities: { [guid: string]: number };
    let layoutQuantities: { [guid: string]: number };

    const quantityOption = options.quantity ?? this.props.quantityOption;

    switch (quantityOption) {
      case QuantityOption.Complete: {
        overallQuantities = quantitiesFor(overallQuantityMap, 'fullQuantity');
        layoutQuantities = quantitiesFor(layoutQuantityMap, 'fullQuantity');
        break;
      }

      case QuantityOption.OneOfTopLevel: {
        overallQuantities = quantitiesFor(overallQuantityMap, 'oneOfParent');
        layoutQuantities = quantitiesFor(layoutQuantityMap, 'oneOfParent');
        break;
      }

      case QuantityOption.Custom: {
        overallQuantities = mapValues(quantitiesFor(overallQuantityMap, 'fullQuantity'), () => 1);
        layoutQuantities = mapValues(quantitiesFor(layoutQuantityMap, 'fullQuantity'), () => 1);
        break;
      }

      default:
        assertExhaustiveSwitch(quantityOption, 'Unhandled QuantityOption');
    }

    const layoutQuantitiesApplicable = [...Object.values(layoutQuantities)].some((quantity) => quantity > 0);
    let onlyUseLayoutQuantities: boolean;

    this.setState(
      (state) => {
        // if mounting, set initial value based on whether layout quantities are applicable, otherwise preserve current
        //   value unless prohibited by layout quantities not being applicable
        onlyUseLayoutQuantities = options.mounting
          ? state.layoutQuantitiesApplicable
          : state.layoutQuantitiesApplicable
            ? state.onlyUseLayoutQuantities
            : false;
        return { layoutQuantitiesApplicable, onlyUseLayoutQuantities };
      },
      () => {
        this.props.onQuantityChange(
          quantityOption,
          layoutQuantitiesApplicable && onlyUseLayoutQuantities ? layoutQuantities : overallQuantities,
        );
      },
    );
  };

  setCustomQuantity = (componentDesignGuid: string, quantity: number) => {
    this.props.onQuantityChange(QuantityOption.Custom, {
      ...this.props.quantities,
      [componentDesignGuid]: quantity,
    });
  };

  setQuantities = (quantities: { [guid: string]: number }) => {
    this.props.onQuantityChange(this.props.quantityOption, {
      ...this.props.quantities,
      ...quantities,
    });
  };

  render() {
    return (
      <Box sx={{ maxWidth: 500, maxHeight: 400, overflowY: 'auto' }}>
        <Select
          variant="outlined"
          fullWidth
          sx={{ mb: 3 }}
          value={this.props.quantityOption}
          onChange={this.onChangeQuantityOption}
        >
          <MenuItem value={QuantityOption.Complete}>Based on quantities of everything selected</MenuItem>
          <MenuItem value={QuantityOption.OneOfTopLevel}>Based on quantities for top level selection</MenuItem>
          <MenuItem value={QuantityOption.Custom}>Custom number (edit values below)</MenuItem>
        </Select>

        {this.state.layoutQuantitiesApplicable && this.props.quantityOption !== QuantityOption.Custom && (
          <FormGroup sx={{ marginBottom: 2 }}>
            <FormControlLabel
              label="Include components not assigned to truss envelopes"
              control={
                <Checkbox
                  checked={!this.state.onlyUseLayoutQuantities}
                  onChange={(_, checked) => {
                    this.setState({ onlyUseLayoutQuantities: !checked }, this.onChangeOptions);
                  }}
                />
              }
            />
          </FormGroup>
        )}

        {this.props.quantityOption === QuantityOption.Custom && (
          <CountFileUploader quantities={this.props.quantities} onQuantityChange={this.setQuantities} />
        )}

        <ComponentQuantityList
          quantities={this.props.quantities}
          setCustomQuantity={this.setCustomQuantity}
          disabled={this.props.quantityOption !== QuantityOption.Custom}
        />
      </Box>
    );
  }
}

type StateProps = ReturnType<typeof mapStateToProps>;

function mapStateToProps(state: RootState) {
  return {
    overallQuantityMap: getQuantityMap(state),
    layoutQuantityMap: getLayoutQuantityMap(state),
  };
}

export const ChooseQuantities = connect(mapStateToProps)(ChooseQuantitiesComponent);
